import { Checkbox } from 'module/ui/components/atoms/checkbox';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Input } from 'module/ui/components/atoms/input/input';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  faChevronDown,
  faRotateRight,
  faGripDotsVertical,
} from '@fortawesome/pro-solid-svg-icons';
import * as Popover from '@radix-ui/react-popover';
import { Button } from '@rocketlane/ui';
import { SignalConfiguration } from 'api/signal/types';
import classNames from 'classnames';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import * as React from 'react';
import { FormattedMessage } from 'react-intl';

interface MultiSelectSignalsProps {
  signals?: SignalConfiguration[];
  selectedSignals?: number[];
  onSelectionChange?: (selectedSignals: number[]) => void;
  onSignalsReorder?: (reorderedSignals: SignalConfiguration[]) => void;
  isLoading?: boolean;
  title?: string;
  placeholder?: string;
  // External trigger control
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Custom trigger
  trigger?: React.ReactNode;
  showTrigger?: boolean;
}

// Sortable signal item component
const SortableSignalItem: React.FC<{
  signal: SignalConfiguration;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ signal, isSelected, onToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: signal.signalConfigurationId.toString() });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || undefined,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={classNames(
        'flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-gray-100',
        { 'bg-gray-50': isDragging },
      )}
    >
      <Checkbox checked={isSelected} onChange={onToggle} />
      <span className="flex-1">{signal.signalName}</span>
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 hover:bg-gray-200 rounded"
      >
        <FontAwesomeIcon
          icon={faGripDotsVertical}
          className="h-3 w-3 text-gray-400"
        />
      </div>
    </div>
  );
};

export const MultiSelectSignals: React.FC<MultiSelectSignalsProps> = ({
  signals = [],
  selectedSignals = [],
  onSelectionChange,
  onSignalsReorder,
  isLoading = false,
  title = 'Manage signals',
  placeholder = 'Search',
  open: externalOpen,
  onOpenChange: externalOnOpenChange,
  trigger,
  showTrigger = true,
}) => {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  // Use external or internal state
  const isControlled = externalOpen !== undefined;
  const open = isControlled ? externalOpen : internalOpen;
  const setOpen = isControlled ? externalOnOpenChange : setInternalOpen;

  // If no signals provided, fetch them
  const { data: fetchedSignals, isLoading: isFetching } =
    useGetAllSignalConfigurationsQuery({
      enabled: signals.length === 0,
    });

  const allSignals = signals.length > 0 ? signals : fetchedSignals || [];
  const isDataLoading = isLoading || isFetching;

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const toggleOption = (value: number) => {
    const newSelected = selectedSignals.includes(value)
      ? selectedSignals.filter((v) => v !== value)
      : [...selectedSignals, value];

    onSelectionChange?.(newSelected);
  };

  const toggleAll = () => {
    if (selectedSignals.length === allSignals.length) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(allSignals.map((s) => s.signalConfigurationId));
    }
  };

  const resetToDefault = () => {
    // Reset to empty selection (no default signals selected)
    onSelectionChange?.([]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = allSignals.findIndex(
        (signal) => signal.signalConfigurationId.toString() === active.id,
      );
      const newIndex = allSignals.findIndex(
        (signal) => signal.signalConfigurationId.toString() === over?.id,
      );

      const reorderedSignals = arrayMove(allSignals, oldIndex, newIndex);

      onSignalsReorder?.(reorderedSignals);
    }
  };

  const filteredOptions = allSignals.filter((opt) =>
    opt.signalName.toLowerCase().includes(search.toLowerCase()),
  );

  const allChecked =
    selectedSignals.length === allSignals.length && allSignals.length > 0;

  const indeterminate =
    selectedSignals.length > 0 && selectedSignals.length < allSignals.length;

  const defaultTrigger = (
    <Button
      variant="secondary"
      className="flex w-60 items-center justify-between rounded-2xl border px-3 py-2 shadow-sm text-sm focus:outline-none"
      rightIcon={
        <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 opacity-50" />
      }
    >
      <span>
        {selectedSignals.length > 0
          ? `${selectedSignals.length} selected`
          : title}
      </span>
    </Button>
  );

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      {showTrigger && (
        <Popover.Trigger asChild>{trigger || defaultTrigger}</Popover.Trigger>
      )}
      <Popover.Content
        align="end"
        side="bottom"
        sideOffset={8}
        className="z-50 w-72 rounded-2xl border bg-white shadow-md"
      >
        <div className="p-3 border-b">
          <div className="font-medium text-sm mb-2">{title}</div>
          <Input
            placeholder={placeholder}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border px-2 py-1 text-sm focus:outline-none"
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {isDataLoading ? (
            <div className="p-3 text-sm text-gray-500">Loading...</div>
          ) : (
            <>
              {/* Bulk Select */}
              <div className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-gray-100 font-medium">
                <Checkbox
                  checked={allChecked}
                  indeterminate={indeterminate}
                  onChange={toggleAll}
                  style={{
                    lineHeight: '1',
                  }}
                />
                <span onClick={toggleAll}>All signals</span>
              </div>

              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={filteredOptions.map((signal) =>
                    signal.signalConfigurationId.toString(),
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredOptions.map((opt) => (
                    <SortableSignalItem
                      key={opt.signalConfigurationId}
                      signal={opt}
                      isSelected={selectedSignals.includes(
                        opt.signalConfigurationId,
                      )}
                      onToggle={() => toggleOption(opt.signalConfigurationId)}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </>
          )}
        </div>
        <div className="border-t p-2">
          <Button
            variant="ghost"
            leftIcon={
              <FontAwesomeIcon icon={faRotateRight} className="h-4 w-4" />
            }
            onClick={resetToDefault}
            className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm hover:bg-gray-100"
          >
            <FormattedMessage id="reset_to_default" />
          </Button>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};
